import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';
import { Col, Form, Row } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LD_CLAIM_DUPLICATE_STATUS,
  LD_CLAIM_DUPLICATE_STATUS_MESSAGE,
  PRE_CLAIMS_CREATE_DEFAULT_VALUES,
} from '../../../../contants/ClaimsEnums';

import {
  createPreClaimsInProgress,
  getPreClaimsDuplicatesInProgress,
} from '../../../../redux/Claims/PreClaims/PreClaimsActions';
import { setPageBlocker } from '../../../../redux/common/CommonActions';
import { valueToNumber, valueToString } from '../../../../utilz';
import { convertPreClaimsFilesToAPI } from '../../../../utilz/PreClaimsUtils';
import { getFormattedTimeStringWithoutTimeZone } from '../../../../utilz/dates';
import { routeFileSelector } from '../../../../utilz/i18n';
import FormLabel from '../../../components/Labels/FormLabel';
import SecondaryLabel from '../../../components/Labels/SecondaryLabel';
import AlertModal from '../../../components/Modals/AlertModal';
import ConfirmationModal from '../../../components/Modals/ConfirmationModal';
import Paper from '../../../components/Paper/Paper';
import ContentSpinner from '../../../components/Spinners/ContentSpinner';
import SaveCancelButtons from '../../Shipment/components/SaveCancelButtons';
import { useGetPreClaimsInitialValues } from '../hooks';
import CreatePreClaimsTab from './CreateTabs/CreatePreClaimsTab';

const CreatePreClaimsScreen = () => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const invoiceAndFbNoRef = useRef({
    invoiceNo: undefined,
    claimProNo: undefined,
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const changeCompany = useSelector((state) => state.auth.changeCompany);
  const [activeKey, setActiveKey] = useState(['1', '2', '3', '4']);
  const [conformationModal, setConformationModal] = useState(false);
  const [itemList, setItemList] = useState(PRE_CLAIMS_CREATE_DEFAULT_VALUES.ITEMS);
  const [notesList, setNotesList] = useState(PRE_CLAIMS_CREATE_DEFAULT_VALUES.NOTES);
  const [filesList, setFilesList] = useState(PRE_CLAIMS_CREATE_DEFAULT_VALUES.FILES);
  const [submitForm] = Form.useForm();
  const [detailsForm] = Form.useForm();
  const [isInstantDuplicateValidation, setIsInstantDuplicateValidation] = useState(false);
  const [preClaimCreatedNumber, setPreClaimCreatedNumber] = useState(false);
  const isInitialRenderRef = useRef(true);
  const { selectedCompany } = useSelector((state) => state.auth);

  const { data: initialValues, isFetching: isInitialValuesLoading } = useGetPreClaimsInitialValues(
    selectedCompany?.companyId,
  );

  useEffect(() => {
    if (changeCompany.success && !isInitialRenderRef.current) {
      setItemList(PRE_CLAIMS_CREATE_DEFAULT_VALUES.ITEMS);
      setNotesList(PRE_CLAIMS_CREATE_DEFAULT_VALUES.NOTES);
      setFilesList(PRE_CLAIMS_CREATE_DEFAULT_VALUES.FILES);
      submitForm.resetFields();
      detailsForm.resetFields();
    }
    isInitialRenderRef.current = false;
  }, [changeCompany]);

  const handleFormProviderSubmit = (form) => {
    if (form === 'saveButtonForm') {
      initiateSubmitProcess();
    }
  };

  const initiateSubmitProcess = async (isInstantValidation, changedFieldName) => {
    try {
      if (!isInstantValidation) await detailsForm.validateFields();
      const basicDetails = detailsForm.getFieldsValue();
      if (isInstantValidation && invoiceAndFbNoRef.current[changedFieldName] === basicDetails[changedFieldName]) {
        return;
      }
      invoiceAndFbNoRef.current = {
        invoiceNo: basicDetails.invoiceNo,
        claimProNo: basicDetails.claimProNo,
      };

      const itemListL = itemList?.filter(({ isNew }) => !isNew);
      if (itemListL?.length === 0 && !isInstantValidation) {
        showNotification({
          type: NOTIFICATION_TYPES.ERROR,
          message: 'Minimum one claim item is required.',
        });
        return false;
      }

      const handleDone = (statusCode) => {
        if (Number(statusCode) === LD_CLAIM_DUPLICATE_STATUS.NEW) {
          if (!isInstantValidation) createClaim(payload);
          return;
          // biome-ignore lint/style/noUselessElse: <explanation>
        } else {
          if (LD_CLAIM_DUPLICATE_STATUS_MESSAGE[statusCode]) {
            if (isInstantValidation) setIsInstantDuplicateValidation(true);
            setConformationModal(LD_CLAIM_DUPLICATE_STATUS_MESSAGE[statusCode]);
          }
        }
      };

      const payload = {
        claimKey: 0,
        freightBillNo: valueToString(basicDetails?.claimProNo),
        invoiceNo: valueToString(basicDetails?.invoiceNo),
        handleDone,
        isInstantValidation,
      };

      dispatch(getPreClaimsDuplicatesInProgress(payload));
    } catch {
      showNotification({
        type: NOTIFICATION_TYPES.ERROR,
        message: 'Some of the fields having error,  Kindly validate it Again.',
      });
    }
  };

  const handleClaimCreationDone = (claimKey) => {
    dispatch(setPageBlocker(false));
    setPreClaimCreatedNumber(claimKey);
  };

  const createClaim = async () => {
    try {
      await detailsForm.validateFields();
      const basicDetails = detailsForm.getFieldsValue();
      const payload = {
        claimKey: 0,
        carrierKey: valueToNumber(basicDetails?.carrierKey),
        branchKey: valueToNumber(basicDetails?.branchKey),
        claimNumber: '',
        invoiceNo: valueToString(basicDetails?.invoiceNo),
        claimProNo: valueToString(basicDetails?.claimProNo),
        claimTypeKey: valueToNumber(basicDetails?.claimTypeKey),
        reasonType: valueToString(basicDetails?.reasonType),
        reasonCode: valueToString(basicDetails?.reasonCode),
        consigneeKey: valueToNumber(basicDetails?.consigneeKey),
        consigneeShortName: valueToString(basicDetails?.consigneeShortName),
        consigneeName: valueToString(basicDetails?.consigneeName),
        consigneeAddr1: valueToString(basicDetails?.consigneeAddr1),
        consigneeAddr2: valueToString(basicDetails?.consigneeAddr2),
        consigneeCity: valueToString(basicDetails?.consigneeCity),
        consigneeState: valueToString(basicDetails?.consigneeState),
        consigneeZip: valueToString(basicDetails?.consigneeZip),
        consigneeCountry: valueToString(basicDetails?.consigneeCountry),
        originKey: valueToNumber(basicDetails?.originKey),
        shipperShortName: valueToString(basicDetails?.shipperShortName),
        shipperName: valueToString(basicDetails?.shipperName),
        shipperAddr1: valueToString(basicDetails?.shipperAddr1),
        shipperAddr2: valueToString(basicDetails?.shipperAddr2),
        shipperCity: valueToString(basicDetails?.shipperCity),
        shipperState: valueToString(basicDetails?.shipperState),
        shipperZip: valueToString(basicDetails?.shipperZip),
        shipperCountry: valueToString(basicDetails?.shipperCountry),
        cargoShipmentWeight: valueToNumber(basicDetails?.cargoShipmentWeight),
        dateShipped: getFormattedTimeStringWithoutTimeZone(basicDetails?.dateShipped),
        claimNoOfPieces: valueToNumber(basicDetails?.claimNoOfPieces),
        currencyCode: valueToString(basicDetails?.currencyCode),
        claimStatusCode: valueToString(basicDetails?.claimStatusCode),
        claimComment: [],
        claimItem: [],
        claimFile: [],
      };
      const itemListL = itemList?.filter(({ isNew }) => !isNew);
      if (itemListL?.length === 0) {
        showNotification({
          type: NOTIFICATION_TYPES.ERROR,
          message: 'Minimum one claim item is required.',
        });
        return false;
        // biome-ignore lint/style/noUselessElse: <explanation>
      } else {
        payload.claimItem = itemListL.map((row) => ({
          claimKey: 0,
          itemSeq: valueToNumber(row.itemSeq),
          itemCode: valueToString(row.itemCode),
          itemQuantity: valueToNumber(row.itemQuantity),
          itemDesc: valueToString(row.itemDesc),
          itemUnitMeasure: valueToString(row.itemUnitMeasure),
          fullRelFlag: valueToString(row.fullRelFlag),
          dispositionMethod: valueToString(row.dispositionMethod),
          value: valueToNumber(row.value),
          smType: valueToString(row.smType),
          smFlag: valueToString(row.smFlag),
          uomDesc: valueToString(row.uomDesc),
          smAmount: valueToNumber(row.smAmount),
          claimantValue: valueToNumber(row.claimantValue),
          itemUnitWeight: valueToNumber(row.itemUnitWeight),
          dirtyFlag: 'i',
        }));
      }
      const notesListL = notesList?.filter(({ isNew }) => !isNew);
      if (notesListL?.length > 0) {
        payload.claimComment = notesListL.map((row) => ({
          claimKey: 0,
          commentType: valueToString(row.commentType),
          commentText: valueToString(row.commentText),
          sequenceNo: valueToNumber(row.sequenceNo),
          oldCommentType: '',
          dirtyFlag: 'i',
        }));
      }
      const filesListL = filesList?.filter(({ isNew }) => !isNew);
      if (filesListL?.length > 0) {
        payload.claimFile = convertPreClaimsFilesToAPI(filesListL);
      }
      payload.handleDone = handleClaimCreationDone;
      dispatch(createPreClaimsInProgress(payload));
    } catch {
      showNotification({
        type: NOTIFICATION_TYPES.ERROR,
        message: 'Some of the fields having error,  Kindly validate it Again.',
      });
    }
  };

  const handleDuplicateValidationCancel = () => {
    setIsInstantDuplicateValidation(false);
    setConformationModal(false);
  };

  const handleDuplicateValidationApprove = () => {
    if (!isInstantDuplicateValidation) {
      createClaim();
    }
    handleDuplicateValidationCancel();
  };

  const handleClaimCreationConfirmationAlertClose = () => {
    detailsForm.setFieldsValue(initialValues || {});
    setItemList(PRE_CLAIMS_CREATE_DEFAULT_VALUES.ITEMS);
    setNotesList(PRE_CLAIMS_CREATE_DEFAULT_VALUES.NOTES);
    setFilesList(PRE_CLAIMS_CREATE_DEFAULT_VALUES.FILES);
    setPreClaimCreatedNumber(false);
    invoiceAndFbNoRef.current = {
      invoiceNo: undefined,
      claimProNo: undefined,
    };
  };

  const onFieldsChange = () => {
    dispatch(setPageBlocker(true));
  };

  return (
    <>
      <Form.Provider className="overflow-hidden h-100" onFormFinish={handleFormProviderSubmit}>
        <Row className="flex-direction-column flex-nowrap overflow-hidden h-100">
          <Col className="mb-15">
            <Paper className="px-15 py-10 border-radius-bottom-none">
              <Row align="middle" justify="space-between">
                <Col>
                  <Form name="submitForm" form={submitForm}>
                    <Row gutter={15}>
                      <Col>
                        <FormLabel>Claim No : </FormLabel>
                        <SecondaryLabel className="f-14 fw-600">0</SecondaryLabel>
                      </Col>
                      <Col>
                        <FormLabel>Currency : </FormLabel>
                        <SecondaryLabel className="f-14 fw-600">{initialValues?.currencyCode}</SecondaryLabel>
                      </Col>
                    </Row>
                  </Form>
                </Col>
                <Col>
                  <Row align="middle" gutter={15}>
                    <Col>
                      <Form name="saveButtonForm">
                        <SaveCancelButtons
                          saveButtonText={t('translation:default.buttons.save')}
                          isNew
                          onCancel={() => navigate(-1)}
                          idSuffix="shipment"
                        />
                      </Form>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Paper>
          </Col>
          <Col className="flex-1 custom-scroll-bar">
            {isInitialValuesLoading ? (
              <ContentSpinner size="large" />
            ) : (
              <CreatePreClaimsTab
                activeKey={activeKey}
                setActiveKey={setActiveKey}
                detailsForm={detailsForm}
                notesList={notesList}
                setNotesList={setNotesList}
                filesList={filesList}
                setFilesList={setFilesList}
                itemList={itemList}
                setItemList={setItemList}
                initiateSubmitProcess={initiateSubmitProcess}
                onFieldsChange={onFieldsChange}
                initialValues={initialValues}
              />
            )}
          </Col>
        </Row>
      </Form.Provider>
      <ConfirmationModal
        onOk={handleDuplicateValidationApprove}
        onCancel={handleDuplicateValidationCancel}
        onClose={handleDuplicateValidationCancel}
        open={Boolean(conformationModal)}
        cancelButton={isInstantDuplicateValidation}
        description={conformationModal}
        title="Claim Duplicates"
        okText={t('translation:purchase-order-view.confirmationModal.okText')}
      />
      <AlertModal
        open={Boolean(preClaimCreatedNumber)}
        title={t('translation:claims.preClaims')}
        description={t('translation:claims.preClaimCreatedSuccessMessage', {
          claimNo: preClaimCreatedNumber,
        })}
        onClose={handleClaimCreationConfirmationAlertClose}
        onOk={handleClaimCreationConfirmationAlertClose}
      />
    </>
  );
};

export default CreatePreClaimsScreen;
